import { parseReference } from "@medplum/core";
import type { Coding, Invoice, Provenance, ProvenanceAgent, Reference } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import { withOrganizationMetaAccounts } from "@/utils/accounts";

import { buildVersionedTargetProvenance } from "./versioned-target-provenance";

export interface BuildInvoiceProvenanceParams {
  /** The Invoice as it exists after the write, carrying the version this Provenance describes. */
  invoice: Invoice;
  activity: {
    system: string;
    code: string;
    display: string;
  };
  /** Who made the change — the acting user's profile. */
  agentReference: ProvenanceAgent["who"];
  /** Other resources this write touched, appended after the Invoice so it stays `target[0]`. */
  additionalTargets?: Reference[];
  /** Tags merged into `meta.tag`, for example to mark a record as ledger history. */
  tags?: Coding[];
}

/**
 * Builds the Provenance that records one mutation of an Invoice.
 *
 * The record is stamped with the practice that owns the Invoice, read from `Invoice.issuer`, so it
 * lands in that practice's compartment whoever writes it. The history search is compartment-scoped,
 * and an unstamped payload takes its compartment from the writing user's own membership — which for
 * a user who belongs to another practice puts the record outside the search that has to find it.
 * Taking the practice from the Invoice keeps the acting user out of that decision, and works the
 * same in the statement-generation bot as in the app.
 *
 * @param invoice The Invoice after the write, whose `meta.versionId` is stamped on the target.
 * @param activity The mutation being recorded.
 * @param agentReference The acting user, recorded as the performer.
 * @param additionalTargets Other resources this write touched, kept out of `target[0]` so
 *   `getProvenanceTargetVersionId` keeps reading the Invoice.
 * @param tags Merged into `meta.tag` alongside whatever the build already carries.
 * @returns The Provenance to create. Returns it unsaved so the caller owns the write and its failure.
 */
export function buildInvoiceProvenance({
  invoice,
  activity,
  agentReference,
  additionalTargets,
  tags,
}: BuildInvoiceProvenanceParams): Provenance {
  const provenance = buildVersionedTargetProvenance({
    resource: invoice,
    versionExtensionUrl: Extensions.ProvenanceInvoiceVersionId,
    activity,
    agentReference,
  });

  const withTargets = additionalTargets?.length
    ? { ...provenance, target: [...provenance.target, ...additionalTargets] }
    : provenance;

  const withTags = tags?.length
    ? {
        ...withTargets,
        meta: { ...withTargets.meta, tag: [...(withTargets.meta?.tag ?? []), ...tags] },
      }
    : withTargets;

  const practiceOrganizationId = practiceOrganizationIdFrom(invoice);
  return practiceOrganizationId
    ? withOrganizationMetaAccounts(withTags, practiceOrganizationId)
    : withTags;
}

/**
 * The practice that owns an Invoice, as the logical id of its issuing Organization.
 *
 * Read with the resource type checked rather than `resolveId`, because the id is used to build an
 * `Organization/<id>` account reference: an issuer naming another resource type would otherwise file
 * the record under an Organization that does not exist, or that belongs to another practice.
 *
 * @param invoice The Invoice to read.
 * @returns The Organization's id, or undefined when the issuer is absent or names another type.
 */
export function practiceOrganizationIdFrom(invoice: Invoice | undefined): string | undefined {
  if (!invoice?.issuer?.reference) return undefined;

  const [resourceType, id] = parseReference(invoice.issuer);
  return resourceType === "Organization" ? id : undefined;
}
